import type { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { civilDateAt, type CivilDate } from '@/shared/format/date';
import { useNow } from '@/shared/page';
import { DateField } from '@/shared/ui/date-field';
import { FormField } from '@/shared/ui/field';
import { Select } from '@/shared/ui/select';
import { Textarea } from '@/shared/ui/textarea';

import { MultiSelect, type MultiOption } from './internal/multi-select';

/**
 * U20-4: o legado manda `blockUntil` mesmo sem bloqueio. Fica `false` (fiel) até alguém da CreditAPI
 * conferir; com `true`, `toDisapproval` tira a data quando não há bloqueio.
 */
export const SEND_BLOCK_UNTIL_ONLY_WITH_BLOCK: boolean = false; // U20-4

/** Alvo do bloqueio com o nome de tela; o fio (`insertBlock`/`blockType`, `null`/`'not'`) é do mapper do módulo. */
// eslint-disable-next-line lingui/no-unlocalized-strings -- valores técnicos, não texto de UI
export const BLOCK_TARGETS = ['none', 'Person', 'Operation'] as const;
export type BlockTarget = (typeof BLOCK_TARGETS)[number];

/** Valor do legado que revela o texto livre ("Outros motivos"). */
export const OTHER_REASONS = 'OUTROS_MOTIVOS';

/** Os campos do "reprovar com bloqueio" com nome único de tela (06 §6, armadilha 8). */
export interface DisapprovalValues {
  reasons: string[];
  block: BlockTarget;
  /** Data civil ISO (`2026-10-30`). */
  blockUntil: string | null;
  blacklist: string[];
  message: string;
}

/**
 * O que varia entre as 7 cópias do legado (06 §3.e). Os padrões são os do rejeitar do detalhe
 * (`RejectForm.tsx`, status de compliance, crédito, liquidação e vistoria).
 */
export interface DisapprovalOptions {
  /** Motivo obrigatório (detalhe) ou opcional (lote da lista). Padrão: obrigatório. */
  readonly reasonRequired?: boolean;
  /** Sem motivo nenhum (rejeitar instrumento: só o texto). Padrão: com motivo. */
  readonly withReasons?: boolean;
  /** Mostra "Realizar bloqueio?" e a validade. Padrão: sim. */
  readonly withBlock?: boolean;
  /** Blacklist: só com bloqueio do tomador (`person`, padrão), com qualquer bloqueio (`anyBlock`, cancelar) ou nunca. */
  readonly blacklist?: 'person' | 'anyBlock' | false;
  /** Texto livre: `whenOther` (padrão; aparece e é obrigatório com "Outros motivos"), `required` ou `optional`. */
  readonly message?: 'whenOther' | 'required' | 'optional';
  /** Valor que revela o texto livre. Padrão: `OUTROS_MOTIVOS`. */
  readonly otherValue?: string;
  /** Hoje (data civil): a validade do bloqueio não aceita data passada (`disablePast`). */
  readonly today?: CivilDate;
}

interface Visible {
  readonly reasons: boolean;
  readonly block: boolean;
  readonly blockUntil: boolean;
  readonly blacklist: boolean;
  readonly message: boolean;
  readonly messageRequired: boolean;
}

/** Quais campos aparecem para os valores atuais. A mesma regra vale no schema e na saída. */
export function disapprovalVisibility(values: Pick<DisapprovalValues, 'reasons' | 'block'>, options: DisapprovalOptions = {}): Visible {
  const withReasons = options.withReasons !== false;
  const withBlock = options.withBlock !== false;
  const blocking = withBlock && values.block !== 'none';
  const blacklist = options.blacklist ?? 'person';
  const mode = options.message ?? 'whenOther';
  const other = values.reasons.includes(options.otherValue ?? OTHER_REASONS);
  const message = mode !== 'whenOther' || other || !withReasons;
  return {
    reasons: withReasons,
    block: withBlock,
    blockUntil: blocking,
    blacklist: blocking && (blacklist === 'anyBlock' || (blacklist === 'person' && values.block === 'Person')),
    message,
    messageRequired: message && mode !== 'optional',
  };
}

export function disapprovalDefaults(overrides: Partial<DisapprovalValues> = {}): DisapprovalValues {
  return { reasons: [], block: 'none', blockUntil: null, blacklist: [], message: '', ...overrides };
}

/** Schema com as mensagens no idioma ativo; campo escondido não é validado. */
export function disapprovalSchema(i18n: I18n, options: DisapprovalOptions = {}): z.ZodType<DisapprovalValues, DisapprovalValues> {
  const reasonRequired = options.reasonRequired !== false;
  return z
    .object({
      reasons: z.array(z.string()),
      block: z.enum(BLOCK_TARGETS),
      blockUntil: z.string().nullable(),
      blacklist: z.array(z.string()),
      message: z.string(),
    })
    .superRefine((values, context) => {
      const visible = disapprovalVisibility(values, options);
      if (visible.reasons && reasonRequired && values.reasons.length === 0) {
        context.addIssue({ code: 'custom', path: ['reasons'], message: i18n._(msg`Selecione ao menos 1 motivo.`) });
      }
      if (visible.blockUntil) {
        if (!values.blockUntil || !/^\d{4}-\d{2}-\d{2}$/.test(values.blockUntil)) {
          context.addIssue({ code: 'custom', path: ['blockUntil'], message: i18n._(msg`Informe a validade do bloqueio.`) });
        } else if (options.today && values.blockUntil < options.today) {
          context.addIssue({ code: 'custom', path: ['blockUntil'], message: i18n._(msg`A validade do bloqueio não pode estar no passado.`) });
        }
      }
      if (visible.blacklist && values.blacklist.length === 0) {
        context.addIssue({ code: 'custom', path: ['blacklist'], message: i18n._(msg`Selecione ao menos 1 dado para a blacklist.`) });
      }
      if (visible.messageRequired && values.message.trim() === '') {
        context.addIssue({ code: 'custom', path: ['message'], message: i18n._(msg`Digite o motivo.`) });
      }
    });
}

/** Saída com nomes de tela; o mapper do módulo traduz para o fio do endpoint (U02). */
export interface DisapprovalOutput {
  readonly reasons: string[] | undefined;
  readonly block: 'Person' | 'Operation' | null;
  /** Ausente (`undefined`) só com `SEND_BLOCK_UNTIL_ONLY_WITH_BLOCK` e sem bloqueio (U20-4). */
  readonly blockUntil?: string | null;
  readonly blacklist: string[] | undefined;
  readonly message: string | undefined;
}

/**
 * Valores do formulário → saída: campo escondido sai `undefined` (o legado não mandava), e a
 * validade do bloqueio segue a U20-4.
 */
export function toDisapproval(values: DisapprovalValues, options: DisapprovalOptions = {}, sendBlockUntilOnlyWithBlock = SEND_BLOCK_UNTIL_ONLY_WITH_BLOCK): DisapprovalOutput {
  const visible = disapprovalVisibility(values, options);
  const block = visible.block && values.block !== 'none' ? values.block : null;
  const output = {
    reasons: visible.reasons ? values.reasons : undefined,
    block,
    blacklist: visible.blacklist ? values.blacklist : undefined,
    message: visible.message ? values.message : undefined,
  };
  if (sendBlockUntilOnlyWithBlock && block === null) return output;
  return { ...output, blockUntil: values.blockUntil };
}

export interface DisapprovalFieldsProps extends DisapprovalOptions {
  /** Motivos (`GET /RejectionReason` → `{ value: code, label: description }`, ou a lista literal). */
  readonly reasonOptions?: readonly MultiOption[];
  /** Dados que existem no tomador (CPF/CNPJ, e-mail, telefone…), do mapper do módulo. */
  readonly blacklistOptions?: readonly MultiOption[];
  /** "Motivo da reprovação" (padrão) ou "Motivo" (cancelar). */
  readonly reasonLabel?: string;
}

/**
 * O grupo "reprovar com bloqueio" que o legado copiou 7 vezes (P10, 851 linhas): motivo, bloqueio,
 * validade, blacklist e texto livre, com os campos aparecendo conforme a escolha (protótipo
 * `reject_body`). Vai dentro do `Fields` de um `defineAction`, com `disapprovalSchema`.
 */
export function DisapprovalFields({ reasonOptions = [], blacklistOptions = [], reasonLabel, ...options }: DisapprovalFieldsProps) {
  const { t } = useLingui();
  const now = useNow(60_000);
  const { control } = useFormContext<DisapprovalValues>();
  const reasons = useWatch({ control, name: 'reasons' });
  const block = useWatch({ control, name: 'block' });
  const visible = disapprovalVisibility({ reasons, block }, options);
  const today = options.today ?? civilDateAt(new Date(now));
  const blockOptions = [
    { value: 'none', label: t`Não` },
    { value: BLOCK_TARGETS[1], label: t`Tomador` },
    { value: BLOCK_TARGETS[2], label: t`Operação` },
  ];

  const reasonsField = useController({ control, name: 'reasons' });
  const blockField = useController({ control, name: 'block' });
  const untilField = useController({ control, name: 'blockUntil' });
  const blacklistField = useController({ control, name: 'blacklist' });
  const messageField = useController({ control, name: 'message' });

  return (
    <>
      {visible.reasons ? (
        <FormField name="reasons" label={reasonLabel ?? t`Motivo da reprovação`} required={options.reasonRequired !== false} error={reasonsField.fieldState.error?.message}>
          {(field) => <MultiSelect control={field} label={reasonLabel ?? t`Motivo da reprovação`} options={reasonOptions} value={reasonsField.field.value} onChange={reasonsField.field.onChange} onBlur={reasonsField.field.onBlur} />}
        </FormField>
      ) : null}
      {visible.block ? (
        <FormField name="block" label={t`Realizar bloqueio?`} error={blockField.fieldState.error?.message}>
          {(field) => (
            <Select
              id={field.id}
              name={field.name}
              aria-labelledby={field['aria-labelledby']}
              options={blockOptions}
              value={blockField.field.value}
              onChange={(value) => {
                blockField.field.onChange(value);
                blockField.field.onBlur();
              }}
            />
          )}
        </FormField>
      ) : null}
      {visible.blockUntil ? (
        <FormField name="blockUntil" label={t`Validade do bloqueio`} required hint={t`Só datas a partir de hoje.`} error={untilField.fieldState.error?.message}>
          {(field) => <DateField {...field} min={today} value={untilField.field.value} onChange={untilField.field.onChange} onBlur={untilField.field.onBlur} />}
        </FormField>
      ) : null}
      {visible.blacklist ? (
        <FormField name="blacklist" label={t`Incluir na blacklist`} required error={blacklistField.fieldState.error?.message}>
          {(field) => <MultiSelect control={field} label={t`Incluir na blacklist`} options={blacklistOptions} value={blacklistField.field.value} onChange={blacklistField.field.onChange} onBlur={blacklistField.field.onBlur} />}
        </FormField>
      ) : null}
      {visible.message ? (
        <FormField name="message" label={t`Digite o motivo`} required={visible.messageRequired} error={messageField.fieldState.error?.message}>
          {(field) => <Textarea {...field} ref={messageField.field.ref} rows={3} value={messageField.field.value} onChange={(event) => messageField.field.onChange(event.target.value)} onBlur={messageField.field.onBlur} />}
        </FormField>
      ) : null}
    </>
  );
}
